import * as React from "react"

import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"
import { useDecorativeSvgs } from "../../hooks/useDecorativeSvgs"
import { Loader } from "../Loader/Loader"
import { Tooltip, type TooltipProps } from "../Tooltip/Tooltip"

const buttonVariants = cva(
  [
    "inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap",
    "h-control px-3 rounded-md text-sm font-medium [&>svg]:size-3.5",
    "transition-colors duration-150",
    "disabled:cursor-default disabled:opacity-50",
    "aria-busy:cursor-progress",
  ],
  {
    variants: {
      variant: {
        default:
          "bg-coval-contrast text-coval-text-on-contrast hover:bg-coval-contrast-hover disabled:hover:bg-coval-contrast",
        secondary:
          "bg-surface-default border border-surface-1 text-coval-text hover:bg-surface-contrast disabled:hover:bg-surface-default",
        ghost:
          "bg-transparent text-coval-text-muted hover:bg-surface-1 hover:text-coval-text disabled:hover:bg-transparent disabled:hover:text-coval-text-muted",
        danger:
          "bg-coval-danger-surface text-coval-danger-on-surface hover:bg-coval-danger-surface-hover disabled:hover:bg-coval-danger-surface",
        link: "h-auto px-0 bg-transparent text-coval-text underline underline-offset-2 hover:text-coval-text-muted disabled:hover:text-coval-text",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

/**
 * The `Tooltip` knobs a button can forward, minus the ones it owns itself:
 * the content and trigger are the button's, and `triggerDisabled` follows
 * `disabled`, so a caller that could set it would only ever break the pair.
 */
export type ButtonTooltipProps = Omit<
  TooltipProps,
  "content" | "children" | "triggerDisabled"
>

interface ButtonBaseProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  active?: boolean
  /**
   * The label and any icon. An unnamed `<svg>` passed as a direct child, or
   * as a direct child of the `asChild` element, is hidden from assistive
   * tech, since the button's words or `aria-label` name it. An icon wrapped
   * in another element is not reached; mark that wrapper `aria-hidden`.
   */
  children?: React.ReactNode
  /**
   * Marks the button's action as in flight: disables it, sets `aria-busy`,
   * and shows a spinner (replacing the icon on `icon` buttons, prepended to
   * the label otherwise). No spinner is injected with `asChild`, since
   * `Slot` requires exactly one child.
   */
  loading?: boolean
}

interface ButtonIconBaseProps {
  /** Renders as a square, icon-only control. */
  icon: boolean
  /**
   * Shown instead of `tooltip` while the button is disabled or loading, for
   * saying *why* the control is unavailable. Falls back to `tooltip` when a
   * reason would add nothing.
   */
  disabledReason?: React.ReactNode
  /**
   * Forwarded to the underlying `Tooltip`, for the exceptions its own props
   * exist for: a deliberate `side`, feedback that has to outlive a click
   * (`forceOpen`), or a test hook.
   */
  tooltipProps?: ButtonTooltipProps
}

/**
 * What the icon means. Required alongside `icon`, because an icon with no words
 * is only guessable.
 *
 * A plain string is the whole contract on its own: the button renders the
 * `Tooltip` and derives `aria-label` from the same string, so the visible label
 * and the accessible one cannot drift apart. Anything the derivation cannot
 * read — markup, several lines, or an expression that may be `undefined` —
 * has to carry an `aria-label` of its own, since a tooltip that never resolves
 * to a string would otherwise leave the control both unlabelled and
 * unexplained. Requiring it here is what keeps the type from advertising more
 * than the derivation honors.
 */
type ButtonIconLabelProps =
  | { tooltip: string; "aria-label"?: string }
  | { tooltip: React.ReactNode; "aria-label": string }

type ButtonIconProps = ButtonIconBaseProps & ButtonIconLabelProps

interface ButtonTextProps {
  icon?: false
  /**
   * Only icon buttons carry their own tooltip. A labelled button that needs
   * one wraps in `Tooltip` directly, so the two cases stay distinguishable.
   */
  tooltip?: never
  disabledReason?: never
  tooltipProps?: never
}

export type ButtonProps = ButtonBaseProps & (ButtonIconProps | ButtonTextProps)

/**
 * `Omit` collapses a union to the keys its members share, which would quietly
 * drop `tooltip` and its companions from anything derived from `ButtonProps`.
 * This distributes over the union instead, so each branch keeps its own keys.
 */
export type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never

/**
 * The implementation sees one flat prop bag; callers see the union above. The
 * cast below is what puts the union in front of them, so a `<Button icon>` with
 * no `tooltip` is a type error rather than a silent omission.
 */
const ButtonImpl = React.forwardRef<
  HTMLButtonElement,
  ButtonBaseProps & Partial<ButtonIconBaseProps> & { tooltip?: React.ReactNode }
>(
  (
    {
      className,
      variant,
      asChild = false,
      active,
      icon,
      loading,
      disabled,
      tooltip,
      disabledReason,
      tooltipProps,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button"
    // The button's name comes from its words or `aria-label`, so an icon
    // beside them is decoration.
    const setRef = useDecorativeSvgs(ref)
    const isDisabled = disabled || loading
    const hasTooltip = Boolean(icon) && tooltip != null
    const accessibleName =
      props["aria-label"] ??
      (icon && typeof tooltip === "string" ? tooltip : undefined)
    /**
     * A disabled `<button>` takes no focus, so the tooltip hanging off it is
     * reachable by pointer alone — and a disabled control is exactly where a
     * keyboard or screen-reader user most needs to hear why. `Tooltip` stands
     * in for the button instead: focusable, announced as a disabled button
     * under the same name, and described by the tooltip Radix attaches to it.
     */
    const standsInForControl = hasTooltip && isDisabled

    const control = (
      <Comp
        className={cn(
          buttonVariants({ variant }),
          icon && "h-control aspect-square p-0",
          active && "border border-coval-contrast text-coval-contrast",
          className,
        )}
        ref={setRef}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...props}
        aria-label={accessibleName}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading && <Loader size="sm" className="size-3.5 text-current" />}
            {loading && icon ? null : children}
          </>
        )}
      </Comp>
    )

    if (!hasTooltip) {
      return control
    }

    return (
      <Tooltip
        content={(isDisabled && disabledReason) || tooltip}
        {...tooltipProps}
        triggerDisabled={standsInForControl}
      >
        {control}
      </Tooltip>
    )
  },
)

ButtonImpl.displayName = "Button"

const Button = ButtonImpl as React.ForwardRefExoticComponent<
  ButtonProps & React.RefAttributes<HTMLButtonElement>
>

export { Button, buttonVariants }
